import {
  FormFieldConfig,
} from 'dtos';
import {
  FormFieldAssignee,
} from 'dtos/formFieldAssignee';
import isEmpty from 'lodash/isEmpty';
import {
  notEmptyString,
} from '@populateui/utils';
import {
  PdfTemplateField,
} from 'views/EMR/Settings/PdfTemplateBuilder/types';
import {
  isSignatureFamilyFieldType,
  normalizeFieldTypeName,
} from 'views/EMR/Settings/PdfTemplateBuilder/utils/fieldTypes';

export const DEFAULT_HORIZONTAL_ALIGNMENT = 'left';
export const DEFAULT_VERTICAL_ALIGNMENT = 'middle';

type OptionGroupFieldType = 'radioGroup' | 'checkboxGroup';

const OPTION_GROUP_FIELD_TYPES = new Set<OptionGroupFieldType>(['radioGroup', 'checkboxGroup']);

function isOptionGroupFieldType(fieldType: string): fieldType is OptionGroupFieldType {
  return OPTION_GROUP_FIELD_TYPES.has(fieldType as OptionGroupFieldType);
}

function getOptionGroupFieldId(baseId: string, groupType: OptionGroupFieldType) {
  return groupType === 'checkboxGroup'
    ? `checkbox-group-${baseId}`
    : `radio-group-${baseId}`;
}

function getSafeOptionGroupId(
  groupType: OptionGroupFieldType,
  parentId: string | null | undefined,
  childIds: string[] = [],
  fallbackId = '1',
) {
  const normalizedParentId = parentId ?? '';

  return notEmptyString(normalizedParentId) && !childIds.includes(normalizedParentId)
    ? normalizedParentId
    : getOptionGroupFieldId(parentId ?? childIds[0] ?? fallbackId, groupType);
}

function getSafeRadioGroupId(
  parentId: string | null | undefined,
  childIds: string[] = [],
  fallbackId = '1',
) {
  return getSafeOptionGroupId('radioGroup', parentId, childIds, fallbackId);
}

function getSafeCheckboxGroupId(
  parentId: string | null | undefined,
  childIds: string[] = [],
  fallbackId = '1',
) {
  return getSafeOptionGroupId('checkboxGroup', parentId, childIds, fallbackId);
}

function getOptionGroupId(field: Pick<PdfTemplateField, 'groupId' | 'id'>): string {
  return field.groupId ?? field.id;
}

function getOptionGroupFields(
  fields: PdfTemplateField[],
  groupId: string,
  groupType: OptionGroupFieldType,
): PdfTemplateField[] {
  return fields.filter(
    (field) => field.type === groupType && getOptionGroupId(field) === groupId,
  );
}

function getOptionGroupIsRequired(groupFields: PdfTemplateField[]): boolean {
  const firstField = groupFields[0];
  return firstField?.isRequired ?? firstField?.required ?? true;
}

function applyOptionGroupIsRequired(
  groupFields: PdfTemplateField[],
  isRequired: boolean,
): PdfTemplateField[] {
  return groupFields.map((field, index) => ({
    ...field,
    isRequired: index === 0 ? isRequired : false,
    required: index === 0 ? isRequired : false,
  }));
}

function getOptionGroupAssignee(groupFields: PdfTemplateField[]): FormFieldAssignee {
  const firstField = groupFields[0];
  return firstField?.assignee ?? FormFieldAssignee.Patient;
}

function applyOptionGroupAssignee(
  groupFields: PdfTemplateField[],
  assignee: FormFieldAssignee,
): PdfTemplateField[] {
  return groupFields.map((field) => ({
    ...field,
    assignee,
  }));
}

export function getRadioGroupId(field: Pick<PdfTemplateField, 'groupId' | 'id'>): string {
  return getOptionGroupId(field);
}

export function getRadioGroupFields(
  fields: PdfTemplateField[],
  groupId: string,
): PdfTemplateField[] {
  return getOptionGroupFields(fields, groupId, 'radioGroup');
}

export function getRadioGroupIsRequired(groupFields: PdfTemplateField[]): boolean {
  return getOptionGroupIsRequired(groupFields);
}

export function applyRadioGroupIsRequired(
  groupFields: PdfTemplateField[],
  isRequired: boolean,
): PdfTemplateField[] {
  return applyOptionGroupIsRequired(groupFields, isRequired);
}

export function getRadioGroupAssignee(groupFields: PdfTemplateField[]): FormFieldAssignee {
  return getOptionGroupAssignee(groupFields);
}

export function applyRadioGroupAssignee(
  groupFields: PdfTemplateField[],
  assignee: FormFieldAssignee,
): PdfTemplateField[] {
  return applyOptionGroupAssignee(groupFields, assignee);
}

export function getCheckboxGroupId(field: Pick<PdfTemplateField, 'groupId' | 'id'>): string {
  return getOptionGroupId(field);
}

export function getCheckboxGroupFields(
  fields: PdfTemplateField[],
  groupId: string,
): PdfTemplateField[] {
  return getOptionGroupFields(fields, groupId, 'checkboxGroup');
}

export function getCheckboxGroupIsRequired(groupFields: PdfTemplateField[]): boolean {
  return getOptionGroupIsRequired(groupFields);
}

export function applyCheckboxGroupIsRequired(
  groupFields: PdfTemplateField[],
  isRequired: boolean,
): PdfTemplateField[] {
  return applyOptionGroupIsRequired(groupFields, isRequired);
}

export function getCheckboxGroupAssignee(groupFields: PdfTemplateField[]): FormFieldAssignee {
  return getOptionGroupAssignee(groupFields);
}

export function applyCheckboxGroupAssignee(
  groupFields: PdfTemplateField[],
  assignee: FormFieldAssignee,
): PdfTemplateField[] {
  return applyOptionGroupAssignee(groupFields, assignee);
}

function coerceFieldRequiredFlags(field: PdfTemplateField): PdfTemplateField {
  if (!isSignatureFamilyFieldType(field.type)) {
    return field;
  }

  return {
    ...field,
    isRequired: true,
    required: true,
  };
}

function normalizeSingleField(
  field: FormFieldConfig,
  index: number,
): PdfTemplateField {
  const fieldType = normalizeFieldTypeName(field.type);
  const fieldId = field.fieldId ?? `field-${index + 1}`;
  const isRequired = isSignatureFamilyFieldType(fieldType)
    ? true
    : (field.isRequired ?? true);

  return coerceFieldRequiredFlags({
    id: fieldId,
    name: field.name ?? `Field ${index + 1}`,
    type: fieldType,
    pageNumber: field.pageNum ?? 0,
    rect: {
      x: field.x ?? 0,
      y: field.y ?? 0,
      width: field.width ?? 160,
      height: field.height ?? 32,
    },
    assignee: field.assignee ?? FormFieldAssignee.Patient,
    isRequired,
    required: isRequired,
    verticalAlignment: field.verticalAlignment ?? DEFAULT_VERTICAL_ALIGNMENT,
    horizontalAlignment: field.horizontalAlignment ?? DEFAULT_HORIZONTAL_ALIGNMENT,
    fontSize: field.fontSize ?? null,
    aliasId: field.aliasId ?? null,
    ...(fieldType === 'radioGroup' ? {
      groupId: getSafeRadioGroupId(field.fieldId, [fieldId], `${index + 1}`),
      groupName: field.name ?? `Radio Group ${index + 1}`,
    } : {}),
    ...(fieldType === 'checkboxGroup' ? {
      groupId: getSafeCheckboxGroupId(field.fieldId, [fieldId], `${index + 1}`),
      groupName: field.name ?? `Checkbox Group ${index + 1}`,
    } : {}),
  });
}

function normalizeOptionGroupFields(
  field: FormFieldConfig,
  index: number,
  groupType: OptionGroupFieldType,
): PdfTemplateField[] {
  const children = field.children ?? [];
  const groupId = getSafeOptionGroupId(
    groupType,
    field.fieldId,
    children.map((childField: FormFieldConfig) => childField.fieldId ?? ''),
    `${index + 1}`,
  );
  const defaultGroupName = groupType === 'radioGroup'
    ? `Radio Group ${index + 1}`
    : `Checkbox Group ${index + 1}`;
  const groupName = field.name ?? defaultGroupName;
  const isGroupRequired = field.isRequired ?? true;

  return children.map((childField: FormFieldConfig, childIndex: number) => ({
    id: childField.fieldId ?? `${groupId}-${childIndex + 1}`,
    name: childField.name ?? `Option ${childIndex + 1}`,
    type: groupType,
    pageNumber: childField.pageNum ?? field.pageNum ?? 0,
    rect: {
      x: childField.x ?? field.x ?? 0,
      y: childField.y ?? field.y ?? 0,
      width: childField.width ?? field.width ?? 18,
      height: childField.height ?? field.height ?? 18,
    },
    required: childIndex === 0 ? isGroupRequired : false,
    assignee: childField.assignee
      ?? field.assignee
      ?? FormFieldAssignee.Patient,
    isRequired: childIndex === 0 ? isGroupRequired : false,
    format: childField.format ?? field.format ?? null,
    verticalAlignment: childField.verticalAlignment
      ?? field.verticalAlignment
      ?? DEFAULT_VERTICAL_ALIGNMENT,
    horizontalAlignment: childField.horizontalAlignment
      ?? field.horizontalAlignment
      ?? DEFAULT_HORIZONTAL_ALIGNMENT,
    fontSize: childField.fontSize ?? field.fontSize ?? null,
    aliasId: childField.aliasId ?? field.aliasId ?? null,
    groupId,
    groupName,
  }));
}

export function normalizeFormFields(
  fields?: FormFieldConfig[] | null,
): PdfTemplateField[] {
  return fields?.flatMap((field, index) => {
    const fieldType = normalizeFieldTypeName(field.type);
    const children = field.children ?? [];

    if (!isOptionGroupFieldType(fieldType) || isEmpty(children)) {
      return [normalizeSingleField(field, index)];
    }

    return normalizeOptionGroupFields(field, index, fieldType);
  }) ?? [];
}

function buildOptionGroupMap(
  fields: PdfTemplateField[],
  groupType: OptionGroupFieldType,
): Map<string, PdfTemplateField[]> {
  return fields.reduce<Map<string, PdfTemplateField[]>>((acc, field) => {
    if (field.type !== groupType) {
      return acc;
    }

    const groupId = getOptionGroupId(field);
    const groupFields = acc.get(groupId) ?? [];
    groupFields.push(field);
    acc.set(groupId, groupFields);
    return acc;
  }, new Map());
}

function serializeOptionGroupFields(
  groupType: OptionGroupFieldType,
  groupId: string,
  groupFields: PdfTemplateField[],
): FormFieldConfig {
  const firstField = groupFields[0];
  const defaultGroupName = groupType === 'radioGroup' ? 'Radio Group' : 'Checkbox Group';
  const getIsRequired = groupType === 'radioGroup'
    ? getRadioGroupIsRequired
    : getCheckboxGroupIsRequired;

  return {
    fieldId: groupId,
    name: firstField?.groupName ?? firstField?.name ?? defaultGroupName,
    type: groupType,
    pageNum: firstField?.pageNumber ?? 0,
    x: firstField?.rect.x ?? 0,
    y: firstField?.rect.y ?? 0,
    width: firstField?.rect.width ?? 18,
    height: firstField?.rect.height ?? 18,
    aliasId: firstField?.aliasId ?? null,
    verticalAlignment: 'middle',
    horizontalAlignment: 'center',
    fontSize: firstField?.fontSize ?? null,
    assignee: firstField?.assignee,
    isRequired: getIsRequired(groupFields),
    children: groupFields.map((groupField) => ({
      fieldId: groupField.id,
      name: groupField.name,
      type: 'checkbox',
      pageNum: groupField.pageNumber,
      x: groupField.rect.x,
      y: groupField.rect.y,
      width: groupField.rect.width,
      height: groupField.rect.height,
      parentType: groupType,
      horizontalAlignment: 'center',
      verticalAlignment: 'middle',
      fontSize: groupField.fontSize,
      assignee: groupField.assignee ?? firstField?.assignee,
      isRequired: false,
    })),
  };
}

export function serializeFormFields(fields: PdfTemplateField[]): FormFieldConfig[] {
  const radioGroupMap = buildOptionGroupMap(fields, 'radioGroup');
  const checkboxGroupMap = buildOptionGroupMap(fields, 'checkboxGroup');
  const serializedFields: FormFieldConfig[] = [];
  const visitedOptionGroups = new Set<string>();

  fields.forEach((field) => {
    const coercedField = coerceFieldRequiredFlags(field);

    if (!isOptionGroupFieldType(coercedField.type)) {
      serializedFields.push({
        fieldId: coercedField.id,
        name: coercedField.name,
        type: coercedField.type,
        pageNum: coercedField.pageNumber,
        x: coercedField.rect.x,
        y: coercedField.rect.y,
        width: coercedField.rect.width,
        height: coercedField.rect.height,
        aliasId: coercedField.aliasId,
        verticalAlignment: coercedField.verticalAlignment ?? DEFAULT_VERTICAL_ALIGNMENT,
        horizontalAlignment: coercedField.horizontalAlignment ?? DEFAULT_HORIZONTAL_ALIGNMENT,
        fontSize: coercedField.fontSize ?? null,
        assignee: coercedField.assignee,
        isRequired: coercedField.isRequired ?? true,
      });
      return;
    }

    const groupId = getOptionGroupId(coercedField);
    const visitKey = `${coercedField.type}:${groupId}`;
    if (visitedOptionGroups.has(visitKey)) {
      return;
    }

    const groupMap = coercedField.type === 'radioGroup' ? radioGroupMap : checkboxGroupMap;
    const groupFields = groupMap.get(groupId) ?? [coercedField];

    serializedFields.push(serializeOptionGroupFields(coercedField.type, groupId, groupFields));
    visitedOptionGroups.add(visitKey);
  });

  return serializedFields;
}
